import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserCircle, Receipt, CalendarClock, LogIn, Download, Camera, Trash2, Shield, Star, Link2, Images, Users, CreditCard, FileText } from 'lucide-react';
import CropModal from '../components/CropModal';
import { PaymentError } from '../components/PaymentError';
import { PasswordInput } from '../components/PasswordReveal';
import PhotoUploader from '../components/PhotoUploader';
import GroupSiteActions from '../components/GroupSiteActions';

const money = (cents, cur) => `${((cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: (cur || 'aud').toUpperCase() })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null);
const STATUS_STYLE = {
  paid: { background: '#e6f4ea', color: '#2e7d32' },
  pending: { background: '#fff4e5', color: '#8a5a00' },
  failed: { background: '#fdecea', color: '#8a1f11' },
  expired: { background: '#eee', color: '#666' },
};

function OpenInvoices({ getMyInvoices, payInvoice }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => { getMyInvoices().then((d) => setRows(d.invoices || [])).catch(() => setRows([])); }, [getMyInvoices]);
  if (!rows || !rows.length) return null;
  const pay = async (inv) => {
    setBusy(inv.id); setErr('');
    try { const d = await payInvoice(inv.id, inv.pay_token); window.location.href = d.checkout_url; }
    catch (e) { setErr(e?.response?.data?.detail || 'Online payment is not available right now — please pay by bank transfer quoting the invoice number.'); setBusy(''); }
  };
  return (
    <div className="rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'var(--gold)', background: 'rgba(201,161,59,0.10)' }} data-testid="my-open-invoices">
      <h2 className="flex items-center gap-2 font-title text-xl mb-1" style={{ color: 'var(--navy)' }}><FileText size={20} /> {rows.length === 1 ? 'You have an invoice to pay' : `You have ${rows.length} invoices to pay`}</h2>
      <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>Pay securely online by card, or by bank transfer quoting the invoice number.</p>
      <PaymentError err={err} testid="my-invoice-error" className="text-sm mb-3 rounded p-2" />
      <div className="space-y-2">
        {rows.map((inv) => (
          <div key={inv.id} className="bg-white rounded border px-4 py-3 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`my-invoice-${inv.id}`}>
            <div className="min-w-0">
              <div className="font-title" style={{ color: 'var(--ink)' }}>{inv.kind === 'renewal' ? 'Membership renewal' : 'Invoice'} {inv.number} · <span data-testid={`my-invoice-total-${inv.id}`}>${Number(inv.total).toFixed(2)}</span>{inv.prorata ? <span className="ml-1 text-[11px] px-1.5 rounded-full" style={{ background: 'var(--sage)' }}>pro-rata</span> : null}</div>
              <div className="text-xs" style={{ color: 'var(--olive)' }}>{(inv.lines || []).map((l) => l.description).join('; ')}{inv.due_date ? ` · due ${new Date(inv.due_date).toLocaleDateString('en-AU')}` : ''}{inv.overdue ? ' · overdue' : ''}</div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={`/pay/invoice/${inv.id}?t=${inv.pay_token}`} className="text-xs underline" style={{ color: 'var(--navy)' }} data-testid={`my-invoice-view-${inv.id}`}>View</Link>
              <button onClick={() => pay(inv)} disabled={busy === inv.id} className="inline-flex items-center gap-1 px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy === inv.id ? 0.6 : 1 }} data-testid={`my-invoice-pay-${inv.id}`}><CreditCard size={14} /> {busy === inv.id ? 'Opening checkout…' : 'Pay now'}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyAccount() {
  const { user, getMyPayments, getMyInvoices, payInvoice, receiptUrl, mediaUrl, uploadMyPhoto, addPhotoUrl, addGooglePhoto, setPrimaryPhoto, deletePhoto, updateMyProfile, classLabel, getHasPassword, setMyPassword, listMyPhotos, deleteMyPhoto, updateMyPhoto, getMyActivity, getMySubgroups, confirmMyDetails, getMyLog } = useAuth();
  const refreshMe = null;
  const [myLog, setMyLog] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  useEffect(() => { getMyLog().then(setMyLog).catch(() => setMyLog([])); }, [getMyLog]);
  const [myGroups, setMyGroups] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [photoUrl, setPhotoUrl] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState('');
  const [bio, setBio] = useState('');
  const [bioBusy, setBioBusy] = useState(false);
  const [bioSaved, setBioSaved] = useState(false);
  const [shareEmail, setShareEmail] = useState(false);
  const [shareMembership, setShareMembership] = useState(false);
  const [birthdayOptOut, setBirthdayOptOut] = useState(false);
  const [privacyReady, setPrivacyReady] = useState(false);
  const [privacyStatus, setPrivacyStatus] = useState('');
  const [privacyLoadError, setPrivacyLoadError] = useState(false);
  const privacyTouchedRef = useRef(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const [hasPassword, setHasPassword] = useState(null);
  const [pwNew, setPwNew] = useState('');
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [myGallery, setMyGallery] = useState([]);
  const [myActivity, setMyActivity] = useState(null);

  const loadMyGallery = () => { if (user) listMyPhotos().then(setMyGallery).catch(() => setMyGallery([])); };

  const applyPhotoState = (res) => {
    setPhotos((res && res.photos) || []);
    setPhotoUrl(res && res.photo_url ? res.photo_url : '');
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getMyPayments().then((d) => {
      setData(d);
      applyPhotoState(d);
      setBio(d && d.bio ? d.bio : '');
      if (!privacyTouchedRef.current) {
        setShareEmail(!!(d && d.share_email));
        setShareMembership(!!(d && d.share_membership));
        setBirthdayOptOut(!!(d && d.birthday_opt_out));
      }
      setPrivacyLoadError(false);
      setPrivacyReady(true);
    }).catch(() => { setData(null); setPrivacyLoadError(true); }).finally(() => setLoading(false));
  }, [user, getMyPayments]);

  useEffect(() => {
    if (!user) return;
    getMySubgroups().then((g) => setMyGroups(Array.isArray(g) ? g : [])).catch(() => setMyGroups([]));
  }, [user, getMySubgroups]);

  useEffect(() => {
    if (!user) return;
    getHasPassword().then((r) => setHasPassword(!!r.has_password)).catch(() => setHasPassword(null));
    loadMyGallery();
    getMyActivity().then(setMyActivity).catch(() => setMyActivity(null));
  }, [user, getHasPassword]);

  const submitMyPassword = async () => {
    setPwBusy(true); setPwMsg(null);
    try {
      await setMyPassword(pwNew, hasPassword ? pwCurrent : undefined);
      setHasPassword(true);
      setPwNew(''); setPwCurrent('');
      setPwMsg({ ok: true, text: 'Password saved. You can now sign in with your email and this password.' });
    } catch (e) {
      setPwMsg({ ok: false, text: (e && e.response && e.response.data && e.response.data.detail) || 'Could not save password.' });
    }
    setPwBusy(false);
  };

  const runPhotoOp = async (fn) => {
    setPhotoBusy(true); setPhotoErr('');
    try { applyPhotoState(await fn()); }
    catch (e) { setPhotoErr((e && e.response && e.response.data && e.response.data.detail) || 'Something went wrong. Please try again.'); }
    setPhotoBusy(false);
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setPhotoErr('Please choose an image under 10 MB.'); return; }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setPhotoErr('');
    setCropSrc(dataUrl);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onCropConfirm = async (croppedDataUrl) => {
    await runPhotoOp(() => uploadMyPhoto(croppedDataUrl, croppedDataUrl.startsWith('data:image/png') ? 'image/png' : 'image/jpeg'));
    setCropSrc(null);
  };

  const onAddUrl = async () => {
    if (!urlInput.trim()) return;
    await runPhotoOp(() => addPhotoUrl(urlInput.trim()));
    setUrlInput('');
  };
  const onUseGoogle = async () => runPhotoOp(() => addGooglePhoto());
  const onMakePrimary = async (id) => runPhotoOp(() => setPrimaryPhoto(id));
  const onDeletePhoto = async (id) => runPhotoOp(() => deletePhoto(id));

  const savePrivacy = async (field, value) => {
    privacyTouchedRef.current = true;
    if (field === 'share_email') setShareEmail(value); else if (field === 'birthday_opt_out') setBirthdayOptOut(value); else setShareMembership(value);
    setPrivacyStatus('saving');
    try {
      await updateMyProfile({ [field]: value });
      setPrivacyStatus('saved');
    } catch (err) {
      console.error('Privacy save failed', err);
      if (field === 'share_email') setShareEmail(!value); else setShareMembership(!value);
      setPrivacyStatus('error');
    }
  };

  const saveBio = async () => {    setBioBusy(true); setBioSaved(false);
    try { await updateMyProfile({ bio }); setBioSaved(true); }
    catch (e) {
      console.error('Bio save failed', e);
      setPhotoErr('Could not save your profile. Please try again.');
    }
    setBioBusy(false);
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 fade-in text-center">
        <div className="bg-white rounded-lg shadow-md p-8 border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <UserCircle size={44} className="mx-auto mb-3" style={{ color: 'var(--navy)' }} />
          <h1 className="font-title text-2xl mb-2" style={{ color: 'var(--maroon)' }}>My Account</h1>
          <p className="mb-5">Please log in to view your membership and payment history.</p>
          <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
            <LogIn size={16} /> Member Login
          </Link>
        </div>
      </div>
    );
  }

  const expiry = data && data.membership_expiry;
  const txns = (data && data.transactions) || [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 fade-in">
      <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>My Account</h1>
      <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />

      <div className="bg-white rounded-lg p-5 shadow-sm border mb-6 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <div>
          <p className="font-title text-lg">{user.name}</p>
          <Link to="/my-details" className="text-xs underline" style={{ color: 'var(--navy)' }} data-testid="account-my-details-link">Edit my contact, service & membership details →</Link>
          <p className="text-sm" style={{ color: 'var(--olive)' }}>{user.email}</p>
        </div>
        <div className="text-right">
          <span className="px-2.5 py-1 rounded text-sm font-title" style={{ background: 'var(--gold)', color: '#3a2c06' }}>{classLabel(user.memberClass)}</span>
          {expiry && (
            <p className="text-sm mt-2 flex items-center gap-1 justify-end" style={{ color: 'var(--olive)' }}>
              <CalendarClock size={14} /> Membership to {fmtDate(expiry)}
            </p>
          )}
          {data && data.date_joined && (
            <p className="text-xs mt-1" style={{ color: 'var(--olive)' }} data-testid="my-date-joined">Member since {fmtDate(data.date_joined)}</p>
          )}
        </div>
      </div>

      {user && user.details_review_requested_at && !confirmed && (
        <div className="rounded-lg p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" style={{ background: 'rgba(201,161,59,0.18)', border: '1px solid var(--gold)' }} data-testid="details-review-banner">
          <div className="text-sm" style={{ color: 'var(--ink)' }}><b>Please check your details.</b> The committee asked you to review your contact details below. Edit anything that has changed, then confirm.</div>
          <button onClick={async () => { try { await confirmMyDetails(); setConfirmed(true); if (refreshMe) refreshMe(); } catch { /* ignore */ } }} className="px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="details-confirm-btn">My details are correct</button>
        </div>
      )}
      {confirmed && <div className="rounded-lg p-3 mb-6 text-sm" style={{ background: '#e6f4ea', color: '#2e7d32' }} data-testid="details-confirmed-note">Thank you — your details are confirmed.</div>}
      <OpenInvoices getMyInvoices={getMyInvoices} payInvoice={payInvoice} />
      <div className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="my-groups-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="flex items-center gap-2 font-title text-xl" style={{ color: 'var(--navy)' }}><Users size={20} /> My Groups</h2>
          <GroupSiteActions showRequest={false} onCreated={() => getMySubgroups().then((g) => setMyGroups(Array.isArray(g) ? g : [])).catch(() => {})} />
        </div>
        {myGroups.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }}>You're not part of any groups yet.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            {myGroups.map((g) => (
              <li key={g.slug} className="flex items-center justify-between py-2" data-testid={`my-group-${g.slug}`}>
                <div className="flex items-center gap-2">
                  {g.favicon_url && <img src={g.favicon_url} alt="" className="w-5 h-5 rounded" />}
                  <span className="font-title" style={{ color: 'var(--navy)' }}>{g.name}</span>
                  {g.role === 'admin' && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>Group admin</span>}
                </div>
                <div className="flex items-center gap-3">
                  <Link to={`/g/${g.slug}`} className="text-sm font-title" style={{ color: 'var(--maroon)' }}>View</Link>
                  {g.role === 'admin' && <Link to={`/g/${g.slug}/manage`} data-testid={`manage-group-${g.slug}`} className="text-sm font-title" style={{ color: 'var(--navy)' }}>Manage</Link>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="my-profile-card">
        <h2 className="flex items-center gap-2 font-title text-xl mb-4" style={{ color: 'var(--navy)' }}>
          <Camera size={20} /> My Directory Profile
        </h2>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="md:w-64 flex-shrink-0">
            <div className="flex items-center gap-3 mb-3">
              {mediaUrl(photoUrl) ? (
                <img src={mediaUrl(photoUrl)} alt="Your profile" data-testid="my-profile-photo" className="w-24 h-24 rounded-full object-cover" style={{ border: '3px solid var(--gold)' }} />
              ) : (
                <div className="flex items-center justify-center w-24 h-24 rounded-full" style={{ background: 'var(--navy)', border: '3px solid var(--gold)' }}>
                  <Shield size={40} color="var(--gold)" />
                </div>
              )}
              <div className="text-xs" style={{ color: 'var(--olive)' }}>
                {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? 's' : ''} · the starred one shows on your profile` : 'No photos yet'}
              </div>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3" data-testid="my-photo-gallery">
                {photos.map((p) => {
                  const isPrimary = p.url === photoUrl;
                  return (
                    <div key={p.id} className="relative group" data-testid={`my-photo-${p.id}`}>
                      <img src={mediaUrl(p.url)} alt="" className="w-full h-14 object-cover rounded" style={{ border: isPrimary ? '2px solid var(--gold)' : '2px solid transparent' }} />
                      <button onClick={() => onMakePrimary(p.id)} disabled={photoBusy} title={isPrimary ? 'Primary photo' : 'Set as profile photo'} data-testid={`my-photo-primary-${p.id}`}
                        className="absolute -top-1 -left-1 rounded-full p-0.5" style={{ background: isPrimary ? 'var(--gold)' : 'rgba(255,255,255,0.9)' }}>
                        <Star size={12} color={isPrimary ? '#3a2c06' : 'var(--olive)'} fill={isPrimary ? '#3a2c06' : 'none'} />
                      </button>
                      <button onClick={() => onDeletePhoto(p.id)} disabled={photoBusy} title="Remove photo" data-testid={`my-photo-delete-${p.id}`}
                        className="absolute -top-1 -right-1 rounded-full p-0.5" style={{ background: 'var(--maroon)' }}>
                        <Trash2 size={11} color="#fff" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="my-photo-input" />
            <div className="flex flex-col gap-2">
              <button onClick={() => fileRef.current && fileRef.current.click()} disabled={photoBusy} data-testid="my-photo-upload-btn" className="flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                <Camera size={13} /> Upload photo
              </button>
              {user && user.picture && (
                <button onClick={onUseGoogle} disabled={photoBusy} data-testid="my-photo-google-btn" className="flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
                  <img src={user.picture} alt="" className="w-4 h-4 rounded-full object-cover" /> Use my Google photo
                </button>
              )}
              <div className="flex gap-1.5">
                <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Paste image URL…" data-testid="my-photo-url-input" className="flex-1 min-w-0 px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                <button onClick={onAddUrl} disabled={photoBusy || !urlInput.trim()} data-testid="my-photo-url-btn" className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-title" style={{ background: 'var(--olive)', color: 'var(--cream)' }}>
                  <Link2 size={13} /> Add
                </button>
              </div>
            </div>
            {photoErr && <p className="text-xs mt-2" data-testid="my-photo-err" style={{ color: '#8a1f11' }}>{photoErr}</p>}
          </div>

          <div className="flex-1">
            <label className="text-sm block mb-1" style={{ color: 'var(--olive)' }}>Bio / Service notes</label>
            <textarea
              data-testid="my-bio-input"
              value={bio}
              onChange={(e) => { setBio(e.target.value); setBioSaved(false); }}
              rows={5}
              maxLength={2000}
              placeholder="Share a short bio or your service history — unit(s), postings, deployments, memories…"
              className="w-full px-3 py-2 rounded border text-sm"
              style={{ borderColor: 'rgba(0,0,0,0.2)' }}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs" style={{ color: 'var(--olive)' }}>{bio.length}/2000 · Shown on your directory profile</span>
              <button onClick={saveBio} disabled={bioBusy} data-testid="my-bio-save-btn" className="px-4 py-1.5 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                {bioBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
            {bioSaved && <p className="text-sm mt-1" style={{ color: 'var(--navy)' }} data-testid="my-bio-saved">Profile saved.</p>}

            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-title" style={{ color: 'var(--navy)' }}>Visible to other members</p>
                {privacyStatus === 'saving' && <span className="text-xs" data-testid="privacy-status" style={{ color: 'var(--olive)' }}>Saving…</span>}
                {privacyStatus === 'saved' && <span className="text-xs" data-testid="privacy-status" style={{ color: 'var(--navy)' }}>Saved ✓</span>}
                {privacyStatus === 'error' && <span className="text-xs" data-testid="privacy-status" style={{ color: '#8a1f11' }}>Couldn’t save — try again</span>}
              </div>
              {privacyLoadError && (
                <p className="text-xs mb-2" data-testid="privacy-load-error" style={{ color: '#8a1f11' }}>
                  Couldn’t load your privacy settings. Please refresh the page to try again.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm mb-1.5 cursor-pointer" style={{ opacity: privacyReady ? 1 : 0.5 }}>
                <input type="checkbox" checked={shareEmail} disabled={!privacyReady} data-testid="share-email-toggle"
                  onChange={(e) => savePrivacy('share_email', e.target.checked)}
                  className="w-4 h-4 accent-[var(--maroon)]" />
                Show my email address in the members directory
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ opacity: privacyReady ? 1 : 0.5 }}>
                <input type="checkbox" checked={shareMembership} disabled={!privacyReady} data-testid="share-membership-toggle"
                  onChange={(e) => savePrivacy('share_membership', e.target.checked)}
                  className="w-4 h-4 accent-[var(--maroon)]" />
                Show my membership status &amp; expiry to other members
              </label>
              <p className="text-xs mt-1.5" style={{ color: 'var(--olive)' }}>Off by default. Only financial members can view the directory.</p>
              <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer" style={{ opacity: privacyReady ? 1 : 0.5 }}>
                <input type="checkbox" checked={birthdayOptOut} disabled={!privacyReady} data-testid="birthday-opt-out-toggle"
                  onChange={(e) => savePrivacy('birthday_opt_out', e.target.checked)}
                  className="w-4 h-4 accent-[var(--maroon)]" />
                Don&apos;t send me a birthday greeting email
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="password-card">
        <h2 className="flex items-center gap-2 font-title text-xl mb-2" style={{ color: 'var(--navy)' }}>
          <Shield size={20} /> Sign-in &amp; Password
        </h2>
        {hasPassword === null ? (
          <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
        ) : (
          <>
            <p className="text-sm mb-3" style={{ color: 'var(--olive)' }} data-testid="password-status">
              {hasPassword
                ? 'You can sign in with your email and password, or continue using Google. Change your password below.'
                : 'You currently sign in with Google. Set a password to also sign in with your email address.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              {hasPassword && (
                <PasswordInput value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                  placeholder="Current password" data-testid="current-password-input" className="sm:col-span-2" />
              )}
              <PasswordInput value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                placeholder={hasPassword ? 'New password (min 8 characters)' : 'Choose a password (min 8 characters)'}
                data-testid="new-password-input" className="sm:col-span-2" />
            </div>
            <button onClick={submitMyPassword} disabled={pwBusy || pwNew.length < 8 || (hasPassword && !pwCurrent)}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-title"
              style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: (pwBusy || pwNew.length < 8 || (hasPassword && !pwCurrent)) ? 0.6 : 1 }}
              data-testid="save-password-btn">
              <Shield size={15} /> {hasPassword ? 'Change password' : 'Set password'}
            </button>
            {pwMsg && (
              <p className="text-sm mt-2" data-testid="password-msg" style={{ color: pwMsg.ok ? '#1c6b2c' : '#8a1f11' }}>{pwMsg.text}</p>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="my-photos-card">
        <h2 className="flex items-center gap-2 font-title text-xl mb-2" style={{ color: 'var(--navy)' }}>
          <Images size={20} /> My Photos
        </h2>
        <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>
          Upload photos to your account. Optionally share them with the site &mdash; shared photos are reviewed by an admin before appearing in the Members' Photos gallery.
        </p>
        <PhotoUploader onDone={loadMyGallery} />
        {myGallery.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4" data-testid="my-photos-grid">
            {myGallery.map((p) => (
              <div key={p.id} className="rounded-lg overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid={`my-photo-${p.id}`}>
                <div className="relative">
                  <img src={mediaUrl(p.url)} alt={p.title} className="w-full h-28 object-cover" />
                  <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{
                    background: p.status === 'approved' ? '#e6f4ea' : p.status === 'pending' ? '#fff4e5' : p.status === 'rejected' ? '#fdecea' : 'rgba(255,255,255,0.9)',
                    color: p.status === 'approved' ? '#2e7d32' : p.status === 'pending' ? '#8a5a00' : p.status === 'rejected' ? '#8a1f11' : '#555',
                  }} data-testid={`my-photo-status-${p.id}`}>
                    {p.status === 'approved' ? 'Shared \u2713' : p.status === 'pending' ? 'Pending review' : p.status === 'rejected' ? 'Not approved' : 'Private'}
                  </span>
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold truncate" style={{ color: 'var(--ink)' }}>{p.title || 'Untitled'}</div>
                  {p.status === 'rejected' && p.review_note && <div className="text-[10px] mt-0.5" style={{ color: '#8a1f11' }}>{p.review_note}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={async () => { await updateMyPhoto(p.id, { title: p.title, description: p.description, place: p.place, date_taken: p.date_taken, tags: p.tags, people: p.people, include_location: p.include_location, shared: !p.shared }); loadMyGallery(); }}
                      className="text-[11px] underline" style={{ color: 'var(--navy)' }} data-testid={`my-photo-share-${p.id}`}>
                      {p.shared ? 'Unshare' : 'Share'}
                    </button>
                    <button onClick={async () => { if (window.confirm('Delete this photo?')) { await deleteMyPhoto(p.id); loadMyGallery(); } }}
                      className="text-[11px] underline flex items-center gap-0.5" style={{ color: '#8a1f11' }} data-testid={`my-photo-delete-${p.id}`}>
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div id="log" className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="my-log-card">
        <h2 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>My log</h2>
        <p className="text-xs mb-3" style={{ color: 'var(--olive)' }}>Emails, letters and messages the Association has sent you.</p>
        {myLog === null ? <p className="text-xs">Loading…</p> : myLog.length === 0 ? <p className="text-xs italic" style={{ color: 'var(--olive)' }} data-testid="my-log-empty">Nothing logged yet.</p> : (
          <ul className="divide-y text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid="my-log-list">
            {myLog.slice(0, 50).map((r) => <li key={r.id} className="py-1.5 flex justify-between gap-3"><span className="truncate">{r.subject || r.kind || r.channel}</span><span className="text-xs whitespace-nowrap" style={{ color: 'var(--olive)' }}>{r.channel} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span></li>)}
          </ul>
        )}
      </div>
      {myActivity && (myActivity.total_views > 0 || myActivity.photos_uploaded > 0) && (
        <div className="bg-white rounded-lg p-5 shadow-sm border mb-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="my-activity-card">
          <h2 className="flex items-center gap-2 font-title text-xl mb-3" style={{ color: 'var(--navy)' }}>
            <CalendarClock size={20} /> My Activity
          </h2>
          <div className="flex flex-wrap gap-6 mb-3">
            <div><div className="text-2xl font-title" style={{ color: 'var(--maroon)' }} data-testid="my-activity-views">{myActivity.total_views}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Page views</div></div>
            <div><div className="text-sm font-title" style={{ color: 'var(--navy)' }}>{myActivity.last_seen ? new Date(myActivity.last_seen).toLocaleString() : '—'}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Last visit</div></div>
            <div><div className="text-2xl font-title" style={{ color: 'var(--maroon)' }}>{myActivity.photos_uploaded}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>Photos uploaded ({myActivity.photos_shared} shared)</div></div>
          </div>
          {myActivity.recent && myActivity.recent.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--olive)' }}>Recent visits</p>
              <div className="space-y-1 text-xs">
                {myActivity.recent.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex justify-between gap-2" style={{ color: 'var(--ink)' }}>
                    <span className="truncate" title={r.path}>{r.label || r.path}</span>
                    <span className="whitespace-nowrap" style={{ color: 'var(--olive)' }}>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <h2 className="flex items-center gap-2 font-title text-xl mb-3" style={{ color: 'var(--navy)' }}>
        <Receipt size={20} /> Payment History
      </h2>
      {loading ? (
        <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
      ) : txns.length === 0 ? (
        <div className="bg-white rounded-lg p-6 shadow-sm border text-center" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <p className="mb-3">You have no payments yet.</p>
          <Link to="/support" className="inline-block px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>Renew or Support</Link>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--navy)' }}>
                {['Date', 'Item', 'Amount', 'Status', 'Receipt'].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txns.map((t, i) => (
                <tr key={i} style={{ background: i % 2 ? 'rgba(0,0,0,0.03)' : 'white' }}>
                  <td className="px-4 py-2.5">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-2.5">{t.item_name}</td>
                  <td className="px-4 py-2.5">{money(t.amount, t.currency)}</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded text-xs" style={STATUS_STYLE[t.payment_status] || STATUS_STYLE.pending}>
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {t.payment_status === 'paid' ? (
                      <a href={receiptUrl(t.session_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--navy)' }}>
                        <Download size={13} /> PDF
                      </a>
                    ) : <span style={{ color: 'var(--olive)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cropSrc && (
        <CropModal src={cropSrc} onCancel={() => setCropSrc(null)} onConfirm={onCropConfirm} />
      )}
    </div>
  );
}
